import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, Mail, Phone, CreditCard, AlertTriangle, Clock, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";
import { purchaseStatusLabel } from "../../lib/purchaseStatus";
import { getPaymentBranding } from "../../lib/paymentBranding";
import { failureReason } from "../../lib/paymentReason";

/** Dates that fail to parse render as "—" rather than the literal string
 *  "Invalid Date", which is what a missing field produced here for months. */
function fmtDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("ro-RO");
}

const STATUS_BADGE = {
  paid:                 "bg-[#519872]/15 text-[#519872]",
  pending:              "bg-[#FDE68A] text-[#8a6a1a]",
  pending_registration: "bg-[#965EA7]/15 text-[#7A4986]",
  abandoned:            "bg-[#6B7280]/15 text-[#6B7280]",
  cancelled:            "bg-[#FDBA74]/30 text-[#9A3412]",
  failed:               "bg-[#E63946]/15 text-[#E63946]",
  duplicate:            "bg-[#965EA7]/25 text-[#7A4986]",
};

export default function PurchaseDrawer({ purchaseId, canMutate, currentEmail, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [gateway, setGateway] = useState(null);

  /** Ask EuPlătesc what it thinks happened to this order, right now.
   *
   *  The sweep runs hourly; somebody on the phone with a person who swears
   *  they paid should not have to wait for it. Corrects our status when it is
   *  safe to — never touching `paid` or `refunded` — and sends the customer
   *  nothing either way: announcing an outcome found days late is a decision
   *  for whoever is reading this screen. */
  const checkGateway = async () => {
    setChecking(true);
    try {
      const r = await api.post(`/admin/purchases/${purchaseId}/refresh-status`);
      setGateway(r.data);
      if (!r.data?.ok) {
        toast.error(
          r.data?.error === "no_transaction_found"
            ? "EuPlătesc nu are nicio tranzacție pentru această comandă."
            : r.data?.error || "Verificarea a eșuat.",
        );
      } else if (r.data.verdict === "correct") {
        toast.success(`Status corectat: ${r.data.our_status} → ${r.data.their_status}`);
        await load();
        if (typeof onChanged === "function") onChanged();
      } else if (r.data.verdict === "needs_review") {
        toast.warning("Statusurile diferă, dar comanda e plătită la noi — nu am modificat nimic.");
      } else {
        toast.success("Statusurile se potrivesc.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verificarea a eșuat.");
    } finally {
      setChecking(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/purchases/${purchaseId}/detail`);
      setData(r.data);
    } catch (e) {
      toast.error("Nu am putut încărca detaliile.");
    } finally {
      setLoading(false);
    }
  }, [purchaseId]);
  useEffect(() => { load(); }, [load]);

  const downloadInvoice = async () => {
    try {
      const r = await api.get(`/admin/purchases/${purchaseId}/invoice-pdf`, { responseType: "blob" });
      const series = data?.purchase?.smartbill_series || "";
      const number = data?.purchase?.smartbill_number || "";
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Factura-${series}${number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu am putut descărca factura.");
    }
  };

  const markDuplicate = async () => {
    if (!canMutate) return;
    if (!window.confirm("Confirmi marcarea acestei comenzi ca duplicat?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/purchases/${purchaseId}/mark-duplicate`);
      toast.success("Marcat ca duplicat.");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare.");
    } finally {
      setBusy(false);
    }
  };

  // The IPN payload belongs to whichever processor handled THIS purchase, not
  // to whichever one is active today — an old Netopia row keeps its label after
  // a switch to EuPlătesc.
  const providerLabel = getPaymentBranding(
    data?.purchase?.provider || data?.purchase?.payment_provider
  ).displayName;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="purchase-drawer">
      <button className="flex-1 bg-black/30" aria-label="Închide" onClick={onClose} />
      <aside className="w-full max-w-xl bg-white shadow-2xl overflow-y-auto">
        <header className="sticky top-0 z-10 bg-white border-b border-[#E5E7EB] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#6B7280]">Comandă</div>
            <div className="font-mono text-lg font-semibold text-[#373A45]">#{purchaseId.slice(0, 8)}</div>
          </div>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-[#EDEEF0]" data-testid="purchase-drawer-close"><X size={18} /></button>
        </header>

        {loading && (
          <div className="p-12 text-center text-[#6B7280] inline-flex w-full justify-center gap-2 items-center">
            <Loader2 className="animate-spin" size={18} /> Se încarcă…
          </div>
        )}

        {!loading && data && (
          <div className="p-6 space-y-6">
            {/* Status & meta */}
            <section>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${STATUS_BADGE[data.purchase.status] || ""}`}>
                  {purchaseStatusLabel(data.purchase.status)}
                </span>
                <span className="inline-flex rounded-full px-3 py-1 text-sm font-medium bg-[#F0E7F4] text-[#7A4986]">
                  {data.purchase.mode === "registration_only" ? "Înscriere fără plată" : "Plată online"}
                </span>
                {data.purchase.reconciled_from && (
                  /* The status was corrected after the fact, rather than
                     having always said this. Worth showing: an operator
                     reading "failed" deserves to know it read "abandoned"
                     until the sweep asked the gateway. */
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-[#3F8EBA]/15 text-[#2B6285] px-3 py-1 text-xs font-semibold"
                    data-testid="purchase-drawer-reconciled"
                  >
                    <RefreshCw size={12} /> Corectat din „{data.purchase.reconciled_from}"
                  </span>
                )}
                {data.purchase.possible_duplicate && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#965EA7]/15 text-[#7A4986] px-3 py-1 text-xs font-semibold uppercase tracking-widest">
                    <AlertTriangle size={12} /> Posibil duplicat
                  </span>
                )}
              </div>
              <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <Field label="Sumă" value={data.purchase.mode === "registration_only" ? "—" : `${Number(data.purchase.amount).toLocaleString("ro-RO")} ${data.purchase.currency}`} />
                <Field label="Provider" value={data.purchase.provider || data.purchase.payment_provider || "—"} />
                {/* M901 — show provider-specific identifiers. Netopia rows
                    populate netopia_*, EuPlătesc rows populate euplatesc_*. */}
                {(data.purchase.provider || data.purchase.payment_provider) === "euplatesc" ? (
                  <>
                    <Field label="Nr. comandă EuPlătesc" value={data.purchase.euplatesc_invoice_id || "—"} mono />
                    <Field label="EP ID" value={data.purchase.euplatesc_ep_id || "—"} mono />
                    <Field label="Cod aprobare" value={data.purchase.euplatesc_approval || "—"} mono />
                    <Field label="Action code" value={data.purchase.euplatesc_action ?? "—"} mono />
                  </>
                ) : (
                  <>
                    <Field label="Order ID" value={data.purchase.netopia_order_id || "—"} mono />
                    <Field label="NTP ID" value={data.purchase.netopia_ntp_id || "—"} mono />
                    <Field label="Cod status Netopia" value={data.purchase.netopia_status_code ?? "—"} mono />
                  </>
                )}
                <Field label="Idempotency" value={(data.purchase.idempotency_key || "").slice(0, 8) || "—"} mono />
                <Field label="Creată" value={fmtDate(data.purchase.created_at)} />
                <Field label="Ultim update" value={new Date(data.purchase.updated_at).toLocaleString("ro-RO")} />
              </dl>
            </section>

            {/* SmartBill invoice */}
            {(data.purchase.smartbill_number || data.purchase.smartbill_status) && (
              <section className="border-t border-[#E5E7EB] pt-5">
                <h3 className="text-xs uppercase tracking-widest font-semibold text-[#965EA7] mb-3">Factură SmartBill</h3>
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <Field label="Serie / Număr"
                    value={data.purchase.smartbill_number ? `${data.purchase.smartbill_series || ""} ${data.purchase.smartbill_number}` : "—"} mono />
                  <Field label="Status" value={data.purchase.smartbill_status || "—"} />
                  <Field label="Încasare" value={data.purchase.smartbill_payment_status || (data.purchase.smartbill_collected_at ? "collected" : "—")} />
                  <Field label="Emisă" value={data.purchase.smartbill_invoiced_at ? new Date(data.purchase.smartbill_invoiced_at).toLocaleString("ro-RO") : "—"} />
                </dl>
                {data.purchase.smartbill_error && (
                  <div className="mt-2 text-xs text-[#E63946]">Eroare: {data.purchase.smartbill_error}</div>
                )}
                {data.purchase.smartbill_number && (
                  <button
                    onClick={downloadInvoice}
                    className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#965EA7] px-4 py-2 text-sm font-semibold text-white hover:bg-[#7A4986]"
                    data-testid="purchase-invoice-download"
                  >
                    <CreditCard size={14} /> Descarcă factura
                  </button>
                )}
              </section>
            )}

            {/* Lead linked */}
            {data.lead && (
              <section className="border-t border-[#E5E7EB] pt-5">
                <h3 className="text-xs uppercase tracking-widest font-semibold text-[#965EA7] mb-3">Persoană</h3>
                <div className="space-y-1 text-sm text-[#373A45]">
                  <div className="font-semibold">{data.lead.prenume} {data.lead.nume}</div>
                  <div className="inline-flex items-center gap-2 text-[#6B7280]"><Mail size={14} /> {data.lead.email}</div>
                  <div className="inline-flex items-center gap-2 text-[#6B7280]"><Phone size={14} /> {data.lead.telefon}</div>
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <Field label="Specializare" value={data.lead.specializare} />
                  <Field label="Unitate" value={data.lead.unitate} />
                  <Field label="Grupa" value={data.lead.group_name || "—"} />
                  <Field label="Sursă lead" value={data.lead.source} />
                  <Field label="Status lead" value={data.lead.status} />
                  <Field label="UTM source" value={(data.lead.utm || {}).utm_source || "—"} />
                  <Field label="UTM campaign" value={(data.lead.utm || {}).utm_campaign || "—"} />
                  <Field label="Marketing consent" value={data.lead.consent_marketing ? "Da" : "Nu"} />
                </dl>
              </section>
            )}

            {/* Why the card was refused — read off the purchase itself, so it
                shows for every failure rather than only for the ones whose
                signature we could not verify. The block below still exists and
                still says something different: that we were TOLD a reason we
                could not trust. */}
            {(() => {
              const reason = failureReason(data.purchase);
              if (!reason) return null;
              return (
                <section
                  className="border-t border-[#E5E7EB] pt-5"
                  data-testid="purchase-drawer-failure-reason"
                >
                  <h3 className="text-xs uppercase tracking-widest font-semibold text-[#9b1f2c] mb-3">
                    {reason.cancelled ? "Plată anulată" : "Motivul refuzului"}
                  </h3>
                  <div className="rounded-xl border border-[#E63946]/30 bg-[#E63946]/5 px-3 py-2.5">
                    <div className="text-sm font-semibold text-[#9b1f2c]">
                      {reason.gloss || reason.message}
                    </div>
                    {reason.gloss && (
                      <div className="mt-0.5 text-xs text-[#9b1f2c]/70 font-mono">
                        {reason.message}
                      </div>
                    )}
                    {reason.approval && (
                      <div className="mt-1 text-xs text-[#92400E]">
                        Atenție: refuzul are cod de aprobare ({reason.approval}) —
                        contradictoriu, merită verificat.
                      </div>
                    )}
                  </div>
                </section>
              );
            })()}

            {/* Status history */}
            <section className="border-t border-[#E5E7EB] pt-5">
              <h3 className="text-xs uppercase tracking-widest font-semibold text-[#965EA7] mb-3 inline-flex items-center gap-2">
                <Clock size={14} /> Status history
              </h3>
              <ol className="space-y-2 text-sm">
                {(data.purchase.status_history || []).map((s, i) => (
                  <li key={i} className="rounded-xl bg-[#F5F6F6] border border-[#E5E7EB] px-3 py-2 flex justify-between gap-3">
                    <div className="space-y-0.5">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_BADGE[s.status] || ""}`}>{purchaseStatusLabel(s.status)}</span>
                      <div className="text-xs text-[#6B7280] mt-1">
                        {s.source}{s.message ? ` · ${s.message}` : ""}
                      </div>
                    </div>
                    <div className="text-xs text-[#6B7280] whitespace-nowrap shrink-0">{new Date(s.at).toLocaleString("ro-RO")}</div>
                  </li>
                ))}
              </ol>
            </section>

            {/* Raw IPN */}
            {data.purchase.raw_ipn && (
              <section className="border-t border-[#E5E7EB] pt-5">
                <h3 className="text-xs uppercase tracking-widest font-semibold text-[#965EA7] mb-3 inline-flex items-center gap-2">
                  <CreditCard size={14} /> Payload IPN {providerLabel}
                </h3>
                <pre className="text-xs bg-[#373A45] text-[#F5F6F6] rounded-xl p-3 overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(data.purchase.raw_ipn, null, 2)}
                </pre>
              </section>
            )}

            {/* What the provider told us about a payment we could not verify.
                Shown above the audit list rather than buried in it: this is
                the reason somebody's card was refused, and it is the whole
                point of opening this drawer. Labelled unverified because it
                is — the signature check failed, so nothing here moved the
                purchase's status, and it must not be read as if it had. */}
            {(() => {
              const rejected = (data.audit_logs || [])
                .filter((a) => a.action === "euplatesc_ipn_signature_invalid"
                               && a.details?.claimed_message);
              if (!rejected.length) return null;
              const last = rejected[0];
              return (
                <section
                  className="border-t border-[#E5E7EB] pt-5"
                  data-testid="purchase-drawer-unverified-ipn"
                >
                  <h3 className="text-xs uppercase tracking-widest font-semibold text-[#B45309] mb-3">
                    Raportat de procesator
                  </h3>
                  <div className="rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-3 py-2.5 text-sm text-[#92400E]">
                    <div className="font-semibold">{last.details.claimed_message}</div>
                    <div className="mt-1 text-xs leading-relaxed">
                      {fmtDate(last.ts)}
                      {rejected.length > 1 ? ` · ${rejected.length} notificări` : ""}
                      {" · "}
                      Semnătura notificării nu a putut fi verificată, deci statusul
                      comenzii nu a fost modificat automat.
                    </div>
                  </div>
                </section>
              );
            })()}

            {/* Audit logs */}
            {data.audit_logs && data.audit_logs.length > 0 && (
              <section className="border-t border-[#E5E7EB] pt-5">
                <h3 className="text-xs uppercase tracking-widest font-semibold text-[#965EA7] mb-3">Audit / sistem</h3>
                <ul className="space-y-1 text-xs text-[#6B7280]">
                  {data.audit_logs.slice(0, 10).map((a) => (
                    <li key={a.id} className="rounded-lg bg-[#F5F6F6] border border-[#E5E7EB] px-2 py-1.5">
                      <span className="font-semibold text-[#373A45]">{a.action}</span>
                      {a.actor_email ? ` · ${a.actor_email}` : ""}{" · "}
                      {fmtDate(a.ts)}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Ask the gateway directly */}
            {(data.purchase.provider || data.purchase.payment_provider) === "euplatesc"
              && data.purchase.euplatesc_invoice_id && (
              <section className="border-t border-[#E5E7EB] pt-5">
                <button
                  onClick={checkGateway}
                  disabled={checking}
                  className="inline-flex items-center gap-2 text-sm rounded-full bg-[#EAF2F8] hover:bg-[#D9E8F3] text-[#2B6285] font-semibold px-4 py-2 border border-[#3F8EBA]/30 disabled:opacity-50"
                  data-testid="purchase-drawer-check-gateway"
                >
                  <RefreshCw size={14} className={checking ? "animate-spin" : ""} />
                  {checking ? "Se verifică…" : "Verifică la EuPlătesc"}
                </button>
                <p className="mt-2 text-xs text-[#6B7280] leading-relaxed">
                  Întreabă direct gateway-ul ce status are comanda. Corectează
                  statusul nostru dacă diferă — nu emite factură și nu trimite
                  niciun email către cursant.
                </p>
                {gateway && (
                  <dl
                    className="mt-3 grid grid-cols-2 gap-3 text-sm rounded-xl bg-[#F5F6F6] border border-[#E5E7EB] p-3"
                    data-testid="purchase-drawer-gateway-result"
                  >
                    <Field label="La noi" value={gateway.our_status} />
                    <Field label="La ei" value={gateway.their_status} />
                    <Field label="Motiv" value={gateway.their_message} />
                    <Field
                      label="Rezultat"
                      value={
                        gateway.verdict === "correct" ? "corectat"
                          : gateway.verdict === "agree" ? "se potrivesc"
                          : gateway.verdict === "needs_review" ? "de verificat manual"
                          : gateway.error || "—"
                      }
                    />
                  </dl>
                )}
              </section>
            )}

            {/* Actions */}
            {canMutate && data.purchase.status !== "duplicate" && (
              <section className="border-t border-[#E5E7EB] pt-5">
                <button
                  onClick={markDuplicate}
                  disabled={busy}
                  className="text-sm rounded-full bg-[#FBF4FE] hover:bg-[#F0E7F4] text-[#7A4986] font-semibold px-4 py-2 border border-[#965EA7]/30 disabled:opacity-50"
                  data-testid="purchase-drawer-mark-duplicate"
                >
                  Marchează manual ca duplicat
                </button>
              </section>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-widest text-[#6B7280] font-semibold">{label}</dt>
      <dd className={`mt-0.5 text-sm text-[#373A45] ${mono ? "font-mono" : ""}`}>{value || "—"}</dd>
    </div>
  );
}
